import { useState } from 'react';
import { Skeleton } from '@wayste/shadcn/components/skeleton';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@wayste/shadcn/components/table';
import { cn } from '@wayste/shadcn/lib/utils';
import { SourTableColumn, SourTablePagination } from '@wayste/shadcn/types/sour-table';
import { SourTableColumnHeader } from './sour-table-column-header';
import { SourTablePaginationComponent } from './sour-table-pagination';

export type SourTableProps<T> = {
    columns: SourTableColumn<T>[];
    data: T[];
    pagination?: SourTablePagination;
    isLoading: boolean;
    isError?: boolean;
    errorState?: React.ReactNode;
    onRowClick?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
    rowActions?: (row: T) => React.ReactNode;
    emptyState?: React.ReactNode;
    bodyHeight?: number;
};

export function SourTable<T>({
    columns,
    data,
    pagination,
    isLoading,
    isError,
    errorState,
    onRowClick,
    rowActions,
    emptyState,
    bodyHeight,
}: SourTableProps<T>) {
    const [lastClickedRow, setLastClickedRow] = useState<T | null>(null);

    // Calculate sticky column offsets
    const leftOffsets: (number | undefined)[] = [];
    let runningLeft = 0;
    columns.forEach((col, idx) => {
        if (col.sticky) {
            if (!col.width) {
                throw new Error(`Sticky column "${col.key}" must have a fixed width`);
            }
            leftOffsets[idx] = runningLeft;
            runningLeft += typeof col.width === 'number' ? col.width : parseInt(col.width as string, 10);
        } else {
            leftOffsets[idx] = undefined;
        }
    });

    return (
        <div className="border-border isolate flex size-full flex-col overflow-hidden rounded-md border">
            <div
                className={cn(bodyHeight ? 'overflow-auto' : 'flex-1 overflow-auto')}
                style={bodyHeight ? { height: `${bodyHeight}px` } : undefined}
            >
                <table className="relative w-full caption-bottom text-sm">
                    <TableHeader className="bg-surface-secondary sticky top-0 z-10 shadow-sm">
                        <TableRow>
                            {columns.map((col, idx) => (
                                <TableHead
                                    key={col.key}
                                    className={cn(
                                        col.sticky && 'sticky',
                                        'bg-surface-secondary overflow-hidden text-ellipsis whitespace-nowrap py-3',
                                    )}
                                    style={{
                                        left: col.sticky ? leftOffsets[idx] : undefined,
                                        width: col.width,
                                        minWidth: col.width,
                                        maxWidth: col.width,
                                    }}
                                >
                                    <SourTableColumnHeader column={col} title={col.label} />
                                </TableHead>
                            ))}
                            {rowActions && <TableHead className="bg-surface-secondary px-4 py-2 text-right">Actions</TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isError ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + (rowActions ? 1 : 0)}>
                                    {errorState ?? (
                                        <div className="text-destructive-on-surface-text py-8 text-center">
                                            An error occurred while loading data.
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : isLoading ? (
                            Array.from({ length: pagination?.pageSize ?? 10 }).map((_, i) => (
                                <TableRow key={i} className={cn('transition-all duration-300')}>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            className={cn(col.sticky ? 'bg-surface-tertiary sticky' : '', 'overflow-hidden')}
                                            style={{
                                                left: col.sticky ? leftOffsets[columns.indexOf(col)] : undefined,
                                                width: col.width,
                                            }}
                                        >
                                            <Skeleton className="h-6 w-full" />
                                        </TableCell>
                                    ))}
                                    {rowActions && (
                                        <TableCell className="text-right">
                                            <Skeleton className="ml-auto h-6 w-20" />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        ) : data.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length + (rowActions ? 1 : 0)}
                                    className="hover:bg-surface-tertiary bg-surface-tertiary"
                                >
                                    {emptyState ?? <div className="text-text-tertiary py-8 text-center">No data found.</div>}
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row, rowIdx) => (
                                <TableRow
                                    key={rowIdx}
                                    className={cn('group', onRowClick ? 'cursor-pointer' : '')}
                                    onClick={
                                        onRowClick
                                            ? (e) => {
                                                  setLastClickedRow(row);
                                                  onRowClick(row, e);
                                              }
                                            : undefined
                                    }
                                >
                                    {columns.map((col, colIdx) => (
                                        <TableCell
                                            key={col.key}
                                            className={cn(
                                                col.sticky ? 'sticky' : '',
                                                col.sticky && lastClickedRow === row && 'bg-surface-primary',
                                                'bg-surface-tertiary group-hover:bg-surface-primary overflow-hidden text-ellipsis whitespace-nowrap',
                                                col.cellAlign === 'center' && 'text-center',
                                                col.cellAlign === 'right' && 'text-right',
                                                lastClickedRow === row && 'bg-surface-primary',
                                                col.cellClassName,
                                            )}
                                            style={{
                                                left: col.sticky ? leftOffsets[colIdx] : undefined,
                                                width: col.width,
                                                minWidth: col.width,
                                                maxWidth: col.width,
                                                ...col.cellStyle?.(row),
                                            }}
                                        >
                                            {col.render
                                                ? col.render(row)
                                                : typeof row === 'object' && row !== null && col.key in row
                                                  ? String((row as any)[col.key])
                                                  : ''}
                                        </TableCell>
                                    ))}
                                    {rowActions && <TableCell className="text-right">{rowActions(row)}</TableCell>}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </table>
            </div>
            {pagination && <SourTablePaginationComponent pagination={pagination} />}
        </div>
    );
}
